import {useEffect, useState} from "react";
import {AlertDialog} from "@/primitives/AlertDialog";
import {Button} from "@/primitives/Button";
import {FA6Icon} from "@/primitives/FA6Icon";
import {printQrCode} from "@/features/user/printQrCode";
import {QrCodeCard} from "@/features/user/qrCodeCard";
import {api} from "@/shared/api";

const QR_CODE_IMAGE_URL = "/timr/terminalAuthTokenQRCode.png";

export interface TerminalQrCodeUser {
	id: number;
	name: string;
	login: string;
}

export interface TerminalQrCodeCardProps {
	user: TerminalQrCodeUser;
	/** `false` drops the delete action, for a host that only ever shows a freshly created code. */
	allowDelete?: boolean;
}

export function TerminalQrCodeCard({user, allowDelete = true}: TerminalQrCodeCardProps) {
	/* null = token state not loaded yet */
	const [hasToken, setHasToken] = useState<boolean | null>(null);
	/* seeded per mount, so re-opening the card never resurrects a cached image of a deleted code */
	const [cacheBuster, setCacheBuster] = useState(() => Date.now());
	const [generating, setGenerating] = useState(false);
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
	const [deleting, setDeleting] = useState(false);

	const userId = user.id;
	const params = {userId};
	/* the id is always sent, so the code on screen and the name beside it can never drift apart */
	const downloadUrl = `${QR_CODE_IMAGE_URL}?userId=${userId}`;
	const qrCodeImageUrl = `${downloadUrl}&_=${cacheBuster}`;

	const downloadButton = (
		<Button render={<a href={downloadUrl} target="_self"/>}>
			<FA6Icon icon="download" variant="regular" fixedWidth className="mr-5"/>
			{messages["authToken.button.download"]}
		</Button>
	);
	const printButton = (
		<Button
			onClick={() =>
				printQrCode({
					qrCodeImageUrl,
					name: user.name,
					login: user.login,
					description: messages["authToken.qrCode.card.description"],
				})
			}
		>
			<FA6Icon icon="print" variant="regular" fixedWidth className="mr-5"/>
			{messages["authToken.button.print"]}
		</Button>
	);
	const deleteButton = (
		<Button variant="destructive" onClick={() => setConfirmDeleteOpen(true)}>
			<FA6Icon icon="trash" variant="regular" fixedWidth className="mr-5"/>
			{messages["authToken.button.delete"]}
		</Button>
	);

	useEffect(() => {
		api("getTerminalAuthToken.json")
			.get(params)
			.done((token) => setHasToken(token != null));
	}, [userId]);

	const generate = () => {
		setGenerating(true);
		api("generateTerminalAuthToken.json")
			.post(params)
			.done(() => {
				setCacheBuster(Date.now());
				setHasToken(true);
			})
			.always(() => setGenerating(false));
	};

	const deleteToken = () => {
		setDeleting(true);
		api("deleteTerminalAuthToken.json")
			.post(params)
			.done(() => {
				setHasToken(false);
				setConfirmDeleteOpen(false);
			})
			.always(() => setDeleting(false));
	};

	return (
		<>
			<div className="pl-10 pr-10">
				{hasToken === false && (
					<>
						<p className="content-group">
							{messages["authToken.terminal.generate.info"]}
						</p>
						<div className="text-right">
							<Button onClick={generate} disabled={generating}>
								{generating ? (
									<FA6Icon
										icon="spinner-third"
										variant="regular"
										fixedWidth
										className="spinner mr-5"
									/>
								) : (
									<FA6Icon icon="qrcode" variant="regular" fixedWidth className="mr-5"/>
								)}
								{messages["authToken.terminal.button.generate"]}
							</Button>
						</div>
					</>
				)}
				{hasToken === true && (
					<QrCodeCard
						qrCodeImageUrl={qrCodeImageUrl}
						title={messages["authToken.terminal.qrCode.title"]}
						name={user.name}
						login={user.login}
						description={messages["authToken.qrCode.card.description"]}
						actions={
							<>
								{downloadButton}
								{printButton}
								{allowDelete && deleteButton}
							</>
						}
						enlargedActions={
							<>
								{downloadButton}
								{printButton}
							</>
						}
					/>
				)}
			</div>
			<AlertDialog.Root open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
				{/* the legacy confirm dialog shows the ✕ and closes on Escape, but not on a backdrop click */}
				<AlertDialog.Content variation="warning" size="md" escapeClose>
					<AlertDialog.Header>
						<AlertDialog.Title>
							{messages["authToken.terminal.delete.qrCode.title"]}
						</AlertDialog.Title>
					</AlertDialog.Header>
					<AlertDialog.Body>
						<AlertDialog.Description>
							{messages["authToken.terminal.delete.qrCode.message"]}
						</AlertDialog.Description>
					</AlertDialog.Body>
					<AlertDialog.Footer>
						<Button variant="destructive" onClick={deleteToken} disabled={deleting}>
							<FA6Icon icon="trash" variant="regular" fixedWidth className="mr-5"/>
							{messages["confirmation.delete"]}
						</Button>
						<AlertDialog.Close render={<Button/>}>
							<FA6Icon icon="qrcode" variant="regular" fixedWidth className="mr-5"/>
							{messages["authToken.terminal.delete.qrCode.keep"]}
						</AlertDialog.Close>
					</AlertDialog.Footer>
				</AlertDialog.Content>
			</AlertDialog.Root>
		</>
	);
}
